/**
 * Comparação de zonas de alojamento (módulo puro, sem rede e sem preços).
 *
 * Não inventamos preços nem disponibilidade: comparamos zonas reais por
 * acessibilidade, ligação a transportes e nível de preço relativo, e damos
 * um link de pesquisa para o utilizador confirmar valores atuais.
 */

import { basesForCountry, type RegionalBase } from "./catalog/regions";

export type AccommodationZone = {
  id: string;
  city: string;
  region: string;
  /** 1 = geralmente mais barato, 3 = geralmente mais caro. */
  priceLevel: 1 | 2 | 3;
  priceLabel: string;
  transitNote: string;
  whyHere: string;
  searchUrl: string;
};

const PRICE_LABEL: Record<1 | 2 | 3, string> = {
  1: "Geralmente mais económica",
  2: "Preço intermédio",
  3: "Geralmente mais cara",
};

function searchUrl(city: string, checkIn?: string, checkOut?: string): string {
  const params = new URLSearchParams({ q: `alojamento em ${city}` });
  if (checkIn) params.set("checkin", checkIn);
  if (checkOut) params.set("checkout", checkOut);
  return `https://www.google.com/travel/search?${params.toString()}`;
}

export function zoneFromBase(base: RegionalBase, dates?: { start?: string; end?: string }): AccommodationZone {
  const reasons: string[] = [];
  if (base.transitFriendly) reasons.push("boa ligação de transportes públicos");
  reasons.push(`acesso direto a ${base.region}`);
  if (base.themes.includes("lakes")) reasons.push("junto aos lagos");
  if (base.themes.includes("scenic_rail")) reasons.push("ponto de partida de comboios panorâmicos");
  return {
    id: base.id,
    city: base.city,
    region: base.region,
    priceLevel: base.affordability,
    priceLabel: PRICE_LABEL[base.affordability],
    transitNote: base.transitFriendly
      ? "A pé/comboio a partir da estação principal."
      : "Precisas de carro para te moveres a partir daqui.",
    whyHere: reasons.join(", "),
    searchUrl: searchUrl(base.city, dates?.start, dates?.end),
  };
}

/**
 * Compara 2–4 zonas para o destino, priorizando as mais económicas quando o
 * utilizador pediu preço baixo. Devolve [] quando não há dados curados.
 */
export function compareAccommodationZones(input: {
  countryCode?: string;
  cities?: string[];
  preferAffordable?: boolean;
  dates?: { start?: string; end?: string };
  limit?: number;
}): AccommodationZone[] {
  const pool = basesForCountry(input.countryCode);
  if (!pool.length) return [];
  const wanted = new Set((input.cities ?? []).map((c) => c.toLowerCase()));
  const relevant = wanted.size ? pool.filter((b) => wanted.has(b.city.toLowerCase())) : pool;
  const source = relevant.length >= 2 ? relevant : pool;
  const sorted = [...source].sort((a, b) =>
    input.preferAffordable
      ? a.affordability - b.affordability || Number(b.transitFriendly) - Number(a.transitFriendly)
      : Number(b.transitFriendly) - Number(a.transitFriendly) || a.affordability - b.affordability,
  );
  return sorted.slice(0, Math.max(2, Math.min(input.limit ?? 3, 4))).map((b) => zoneFromBase(b, input.dates));
}

export const ACCOMMODATION_DISCLAIMER =
  "Os preços variam por data e disponibilidade. Confirma sempre no link antes de reservar.";
